import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { renderToStaticMarkup } from 'react-dom/server';
import ServerSideList from '../../../../../../commonComponents/lists/ServerSideList';
import { formatDate } from '../../../../../../services/dateUtilityFunctions';
import translate from '../../../../../../commonComponents/translate';
import ConfigsContext from '../../../../../../contextProviders/ConfigsContext';
import CSRFContext from '../../../../../../contextProviders/CSRFContext';
import Notifications from '../../../../../../services/notifications';
import Loader from '../../../../../../services/loader';

const renderFromInformation = (t, item) => {
  const {
    origin,
    game,
    journey,
    survey,
    session,
    marketOrder,
    academyOrder,
    marketCommission,
    marketCommissionReport,
    marketCommissionCryptoReport,
  } = item;

  switch (origin) {
    case 'marketCommission':
      return t(
        'marketCommission.and.name',
        marketCommission?.date
          ? formatDate(marketCommission.date)
          : t('commission.not.found'),
      );
    case 'marketCommissionDailyForecast':
      return t('daily.forecast');
    case 'marketCommissionReport':
      return t(
        'marketCommissionReport.and.name',
        marketCommissionReport?.recurrenceEndDate
          ? formatDate(
              marketCommissionReport.recurrenceEndDate - 43_200_000,
              'DD MMM, YYYY',
            )
          : t('report.not.found'),
      );
    case 'marketCommissionCryptoReport':
      return t(
        'marketCommissionCryptoReport.and.name',
        marketCommissionCryptoReport?.recurrenceEndDate
          ? formatDate(
              marketCommissionCryptoReport.recurrenceEndDate - 43_200_000,
              'DD MMM, YYYY',
            )
          : t('report.crypto.not.found'),
      );
    case 'withdrawRequest':
      return t('commissions.transactionsList.withdrawRequest');
    case 'custom':
    case 'admin':
      return item.description || t('custom');
    case 'game':
      return t('game.and.name', game?.name || '-');
    case 'tip':
      return t('rewards_tips');
    case 'survey':
      return t('survey.and.name', survey?.title || '-');
    case 'session':
      return t('session.and.name', session?.name || '-');
    case 'marketPlace':
      return academyOrder?.number
        ? t('academyOrder.and.name', academyOrder?.number || '-')
        : t('marketOrder.and.name', marketOrder?.number || '-');
    case 'commissionPlan':
      return t('commissionPlan');
    case 'journey':
      return t('journey.and.name', journey?.title || '-');
    default:
      return 'Type not supported';
  }
};

function CoinsEvolution(props) {
  const { t, filters } = props;

  const { organization } = useContext(ConfigsContext);
  const _csrf = useContext(CSRFContext);
  const { enabled: isCharityCoinsEnabled } = organization?.services?.charity;

  const loader = useRef(null);
  const pollingTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!loader.current) {
      loader.current = new Loader('#myCoinsExportLoader');
    }
    return () => {
      isMountedRef.current = false;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!loader.current) return;
    if (exporting) {
      loader.current.show();
    } else {
      loader.current.hide();
    }
  }, [exporting]);

  const columns = useMemo(() => {
    const columnsToShow = [
      {
        field: 'date',
        textAlign: 'left',
        title: t('shop.date'),
        width: 150,
        sortable: false,
        template: ({ date }) =>
          renderToStaticMarkup(<>{formatDate(date, 'DD MMM YYYY')}</>),
      },
      {
        field: 'coins',
        textAlign: 'left',
        title: t('rewards_coins'),
        width: 150,
        sortable: false,
        template: ({ coins }) =>
          renderToStaticMarkup(<span>{coins || '-'}</span>),
      },
    ];

    if (isCharityCoinsEnabled) {
      columnsToShow.push({
        field: 'charityCoins',
        textAlign: 'left',
        title: t('rewards_charityCoins'),
        width: 150,
        sortable: false,
        template: ({ charityCoins }) =>
          renderToStaticMarkup(<span>{charityCoins || '-'}</span>),
      });
    }

    columnsToShow.push({
      field: 'from',
      textAlign: 'left',
      title: t('rewards_from'),
      width: 250,
      sortable: false,
      template: item =>
        renderToStaticMarkup(<span>{renderFromInformation(t, item)}</span>),
    });
    return columnsToShow;
  }, [isCharityCoinsEnabled]);

  const handleExportList = useCallback(() => {
    const cleanParams = Object.fromEntries(
      Object.entries({
        type: 'coins',
        transactionType: 'payment',
        startDate: filters?.startDate,
        endDate: filters?.endDate,
      }).filter(
        // eslint-disable-next-line no-unused-vars
        ([_, value]) => value !== null && value !== undefined && value !== '',
      ),
    );
    setExporting(true);
    axios
      .post('/jobs/analytics/wallet/transactions/list/xlsx', cleanParams, {
        headers: { 'X-CSRF-Token': _csrf },
      })
      .then(response => {
        const jobId = response?.data?.data?.id;
        if (!jobId) {
          setExporting(false);
          Notifications.showNotificationError(
            'Export',
            'Failed to start export job.',
          );
          return;
        }
        const pollInterval = 2000;
        const checkJobStatus = (retryCount = 0, maxRetries = 10) => {
          if (!isMountedRef.current) return;
          axios
            .get(
              `/jobs/analytics/wallet/transactions/job/${jobId}/status`,
              { timeout: 30000 },
            )
            .then(res => {
              if (!isMountedRef.current) return;
              const { progress, finishedOn, failedReason } =
                res.data.data || res.data;
              if (loader.current) {
                loader.current.showProgressLoader(
                  `${progress?.toFixed(0) || 0}% ${t(
                    'order_status_processing',
                  )}...`,
                );
              }
              if (finishedOn) {
                setExporting(false);
                if (pollingTimeoutRef.current) {
                  clearTimeout(pollingTimeoutRef.current);
                  pollingTimeoutRef.current = null;
                }
                if (failedReason) {
                  Notifications.showNotificationError(
                    'Export failed',
                    failedReason,
                  );
                } else {
                  Notifications.showNotificationSuccess(
                    'Export',
                    'Export completed. Downloading...',
                  );
                  const link = document.createElement('a');
                  link.href = `/excel-jobs/analytics/wallet/transactions/job/${jobId}/download`;
                  link.download = '';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }
              } else {
                pollingTimeoutRef.current = setTimeout(
                  () => checkJobStatus(),
                  pollInterval,
                );
              }
            })
            .catch(() => {
              if (!isMountedRef.current) return;
              if (retryCount < maxRetries) {
                const backoffDelay = Math.min(
                  pollInterval * 2 ** retryCount,
                  30000,
                );
                pollingTimeoutRef.current = setTimeout(
                  () => checkJobStatus(retryCount + 1, maxRetries),
                  backoffDelay,
                );
                return;
              }
              setExporting(false);
              if (pollingTimeoutRef.current) {
                clearTimeout(pollingTimeoutRef.current);
                pollingTimeoutRef.current = null;
              }
              Notifications.showNotificationError(
                'Export',
                'Failed to check export job status. The job may still be processing.',
              );
            });
        };
        checkJobStatus();
      })
      .catch(() => {
        setExporting(false);
        Notifications.showNotificationError(
          'Export',
          'Failed to start export job.',
        );
      });
  }, [filters, _csrf, t]);

  return (
    <div className="my-coins-container">
      <div className="header-container">
        <h5>{t('coins.myCoins')}</h5>
        <button
          type="button"
          onClick={handleExportList}
          disabled={exporting}
          className="btn btn-excel-export"
        >
          {t('export.excel')}
        </button>
      </div>
      {exporting && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(255,255,255,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div id="myCoinsExportLoader" />
        </div>
      )}
      <ServerSideList
        columns={columns}
        id="myCoinsList"
        dataSourceURL="/table/analytics/wallet/transactions/list?type=coins&transactionType=payment"
        dataSourceHeaders={{
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
        }}
        dataSourceParams={{
          populate: [
            'marketCommissionReport',
            'marketCommissionCryptoReport',
            'marketCommission',
            'survey',
            'game',
            'journey',
            'session',
            'marketOrder',
            'academyOrder',
          ],
        }}
        query={filters}
      />
    </div>
  );
}

CoinsEvolution.propTypes = {
  t: PropTypes.func.isRequired,
  filters: PropTypes.shape({
    startDate: PropTypes.number,
    endDate: PropTypes.number,
  }).isRequired,
};

export default translate(CoinsEvolution);
